<template>
    <div>
        <div id="dicom-viewer">
            <div id="viewscroller-container">
                <div id="viewport-container">
                    <div id="dicom-container">
                        <div id="dicom-bottom-right" class="dicom-info" style="bottom:6px; right:15px">
                            <div id="zoom-text">Zoom: </div>
                            <div id="slice-text">Image: </div>
                            <div v-if="loadingProgress && loadingProgress.percentLoaded < 100">
                                Loading: {{loadingProgress.percentLoaded}}%
                            </div>
                        </div>
                        <div id="dicom-bottom-left" class="dicom-info" style="bottom:3px; left:3px">
                            WW/WC:
                        </div>
                    </div>
                    <div class="stack-scroller">
                        <div style="transform: rotate(90deg);">
                            <input type="range" id="stack-range-input">
                        </div>
                    </div>
                </div>
            </div>
            <div id="dicom-control-panel">
                <b>Controls:</b>
                <hr class="soft">
                <br>
                <md-button class="md-raised md-primary md-dense" @click="resetDicomViewport()">
                    Reset Viewport
                </md-button>
                <br><br>
                <md-field class="dicom-window-preset-input">
					<label for="movie">Apply CT Window Preset</label>
                    <md-select @md-selected="usePresetWindow" v-model="currentWindowPreset">
                        <md-option v-for="(windowPreset, presetName) in ctWindows" :key="presetName" :value="presetName">
                            {{presetName.capitalize()}}
                        </md-option>
                    </md-select>
                </md-field>
                <br>
                <hr class="soft">
                <md-button class="md-dense" @click="showUserGuide=!showUserGuide">
                    Toggle User Guide
                </md-button>
                <div v-if="showUserGuide" style="word-wrap: break-word; max-width: 280px; margin: auto; line-height: 1;">
                    <br>
                    Use the scroll bar or scroll on your mouse to traverse the stack.
                    <br><br>
                    Click and drag to adjust window settings or use presets above.
                    <br><br>
                    Right click and drag to zoom. Middle click and drag to pan.
                    <br><br>
                    You can also use your touch screens to navigate the viewer.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    //Import Deps
    import APIRequests from "../APIRequests.js"
    import cornerstone from "cornerstone-core"
    import Hammer from "hammerjs";
    import * as cornerstoneMath from "cornerstone-math";
    import * as cornerstoneTools from "cornerstone-tools";
    import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader"
    import dicomParser from "dicom-parser"
    //Config tools
    cornerstoneTools.external.Hammer = Hammer;
    cornerstoneTools.external.cornerstone = cornerstone;
    cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
    //Config DICOM loader
    cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
    cornerstoneWADOImageLoader.external.cornerstoneMath = cornerstoneMath;
    cornerstoneWADOImageLoader.external.dicomParser = dicomParser;
    var config = {
        webWorkerPath: "/cornerstoneWADOImageLoaderWebWorker.js",
        taskConfiguration: {
            decodeTask: {
            codecsPath: "/cornerstoneWADOImageLoaderCodecs.js"
            }
        }
    }
    cornerstoneWADOImageLoader.webWorkerManager.initialize(config);
    //Utility
    String.prototype.capitalize = function() {
        return this.charAt(0).toUpperCase() + this.slice(1);
    }

    export default {
        props: {
            folderName: {
                type: String
            }
        },
        data: function(){
            return {
                element: undefined,
                currentWindowPreset: "test",
                ctWindows: {
                    "mediastinum": {"center": 40, "width": 400},
                    "lung": {"center": -500, "width": 1500},
                    "bone": {"center": 500, "width": 2000},
                    "brain": {"center": 40, "width": 80},
                    "head": {"center": 90, "width": 350}
                },
                loadingProgress: {},
                showUserGuide: false,
            }
        },  
        methods: {
            usePresetWindow: function(presetName){
                if(presetName == undefined) return
                if(this.ctWindows[presetName] == undefined) return
                let viewport = cornerstone.getViewport(this.element);
                viewport.voi = {windowWidth: this.ctWindows[presetName].width, windowCenter: this.ctWindows[presetName].center}
                this.updateCurrentImage(undefined, viewport)
                setTimeout(() => {
                    this.currentWindowPreset = undefined
                }, 250)
            },
            resetDicomViewport: function(){
                cornerstone.reset(this.element)
            },
            updateCurrentImage: function(imageId, viewport){
                if(!viewport) {
                    viewport = cornerstone.getViewport(this.element);
                }
                if(!imageId) {
                    let stackToolDataSource = cornerstoneTools.getToolState(this.element, 'stack');
                    if (stackToolDataSource === undefined) return
                    let stackData = stackToolDataSource.data[0];
                    imageId = stackData.imageIds[stackData.currentImageIdIndex]
                }
                cornerstone.loadAndCacheImage(imageId).then((image) => {
                    cornerstone.displayImage(this.element, image, viewport);
                });
            },
            initCornerstone: function(imageIds){
                ///Initialize this.element and dicom sources
                this.element = document.getElementById('dicom-container');

                var stack = {
                    currentImageIdIndex : 0,
                    imageIds: imageIds.map((x) => "wadouri:" + x)
                }

                //Update staes on render
                function onImageRendered(e) {
                    var eventData = e.detail;
                    var viewport = eventData.viewport;
                    document.getElementById('dicom-bottom-left').textContent = "WW/WC: " + Math.round(viewport.voi.windowWidth) + "/" + Math.round(viewport.voi.windowCenter);
                    document.getElementById('zoom-text').textContent = "Zoom: " + viewport.scale.toFixed(2);
                }

                this.element.addEventListener('cornerstoneimagerendered', onImageRendered);
                
                function onNewImage() {
                    var newImageIdIndex = stack.currentImageIdIndex;

                    // Update the slider value
                    var slider = document.getElementById('stack-range-input');
                    slider.value = newImageIdIndex;

                    // Populate the current slice span
                    var currentValueSpan = document.getElementById("slice-text");
                    currentValueSpan.textContent = "Image " + (newImageIdIndex + 1) + "/" + imageIds.length;
                }
                this.element.addEventListener('cornerstonenewimage', onNewImage);

                // Initialize range input
                var range = document.getElementById('stack-range-input');

                // Set minimum and maximum value
                range.min = 0;
                range.step = 1;
                range.max = imageIds.length - 1;

                // Set current value
                range.value = stack.currentImageIdIndex;

                //Update viewer with input on slider
                let selectImage = (event) => {
                    // Get the range input value
                    var newImageIdIndex = parseInt(event.currentTarget.value, 10);
                    // Get the stack data
                    var stackToolDataSource = cornerstoneTools.getToolState(this.element, 'stack');
                    if (stackToolDataSource === undefined) {
                        return;
                    }
                    var stackData = stackToolDataSource.data[0];
                    if(newImageIdIndex !== stackData.currentImageIdIndex && stackData.imageIds[newImageIdIndex] !== undefined) {
                        stackData.currentImageIdIndex = newImageIdIndex;
                        this.updateCurrentImage(stackData.imageIds[newImageIdIndex], undefined)
                    }
                }

                // Bind the range slider events
                document.getElementById('stack-range-input').addEventListener("input", selectImage);

                // Deep copy the imageIds
                var loadProgress = {
                    "imageIds": stack.imageIds.slice(0),
                    "total": stack.imageIds.length,
                    "remaining": stack.imageIds.length,
                    "percentLoaded": 0,
                };

                let onImageLoaded = (event) => {
                    let eventData = event.detail;
                    let imageId = eventData.image.imageId;
                    let imageIds = loadProgress["imageIds"];

                    // Remove all instances, in case the stack repeats imageIds
                    for(var i = imageIds.length - 1; i >= 0; i--) {
                        if(imageIds[i] === imageId) {
                        imageIds.splice(i, 1);
                        }
                    }
                    // Populate the load progress object
                    loadProgress["remaining"] = imageIds.length;
                    loadProgress["percentLoaded"] = parseInt(100 - loadProgress["remaining"] / loadProgress["total"] * 100, 10);
                    this.loadingProgress = loadProgress;
                }

                // Image loading events are bound to the cornerstone object, not the element
                cornerstone.events.addEventListener("cornerstoneimageloaded", onImageLoaded);
                let resizeTimeout = undefined
                window.addEventListener("resize", () => {
                    clearTimeout(resizeTimeout)
                    resizeTimeout = setTimeout(() => {
                        cornerstone.resize(this.element)
                    }, 250)
                });

                //Enable cornerstone and load in image
                cornerstone.enable(this.element);
                cornerstoneTools.touchInput.enable(this.element);
                cornerstone.loadAndCacheImage(stack.imageIds[stack.currentImageIdIndex]).then((image) => {
                    cornerstone.displayImage(this.element, image);
                    // Set the stack as tool state
                    cornerstoneTools.addStackStateManager(this.element, ['stack']);
                    cornerstoneTools.addToolState(this.element, 'stack', stack);

                    // Enable our mouse tools
                    cornerstoneTools.mouseInput.enable(this.element);
                    cornerstoneTools.mouseWheelInput.enable(this.element);
                    cornerstoneTools.wwwc.activate(this.element, 1); // Left Click
                    cornerstoneTools.pan.activate(this.element, 2); // Middle Click
                    cornerstoneTools.zoom.activate(this.element, 4); // Right Click
                    //cornerstoneTools.zoomWheel.activate(this.element); // Mouse Wheel
                    //cornerstoneTools.stackScroll.activate(this.element, 1);
                    cornerstoneTools.stackScrollWheel.activate(this.element);
                    //cornerstoneTools.scrollIndicator.enable(this.element);
                    // Enable our touch tools
                    cornerstoneTools.wwwcTouchDrag.activate(this.element);
                    cornerstoneTools.zoomTouchPinch.activate(this.element);
                    cornerstoneTools.panMultiTouch.activate(this.element);
                    cornerstoneTools.stackPrefetch.enable(this.element, 3);
                });
            }
        },
        mounted: function(){
            this.$nextTick(function(){
                if(this.folderName.trim() == "") {
                    alert("Invalid imaging study.")
                    window.location = "/"
                    return
                }
                let api_url = this.$store.state.api_url + "/dicom_metadata/"+ this.folderName
                APIRequests.getJSONData(api_url, this.$store.state.jwtToken).then((data) => {
                    if(!data) {
                        alert("Invalid imaging study.")
                        window.location = "/"
                        return
                    }
                    let cleanData = data.map((filename) => this.$store.state.api_url + "/dicom_data/" + this.folderName + "/" + filename)
                    this.initCornerstone(cleanData)
                })
            })
        }
    }
</script>

<style scoped>
    $dicom-width: 700px;
    $dicom-height: 80vh;
    #dicom-container {
        height: 100%;
        position: relative;
        width: $dicom-width;
        max-width: 80vw;
        margin-right: 40px;
        background-color: #333;
    }
    @media (max-width: 500px) {
        #dicom-container{
            max-width: 88vw;
        }
        #viewscroller-container{
            width: 100%;
        }
    }
    #viewport-container{
        height: $dicom-height;
        position: relative;
        margin-bottom: 20px;
    }
    .dicom-info {
        position: absolute;
        color: white;
    }
    .stack-scroller{
        width: 1px;
        position: absolute;
        right: 25px;
        top: -25px;
    }
    #stack-range-input{
        width: $dicom-height;
    }
    #dicom-viewer{
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: space-around;
    }
    #dicom-control-panel{
        flex-grow: 2;
        padding: 10px 20px;
        border: 1px dotted black;
    }
    .dicom-window-preset-input{
        max-width: 300px;
        margin: auto;
        text-align: center;
    }
</style>

<style scoped>
    input[type=range] {
        -webkit-appearance: none;
        margin: 5px 10px;
        width: 100%;
        background-color: #fafafa;
    }

    input[type=range]:focus {
        outline: none;
    }

    input[type=range]::-webkit-slider-runnable-track {
        width: 100%;
        height: 16px;
        cursor: pointer;
        box-shadow: 0px 0px 0px #000000;
        background: #B6B6B6;
        border-radius: 25px;
        border: 1px solid #8A8A8A;
    }

    input[type=range]::-webkit-slider-thumb {
        box-shadow: 1px 1px 1px #828282;
        border: 1px solid #8A8A8A;
        height: 24px;
        width: 35px;
        border-radius: 6px;
        background: #DADADA;
        cursor: pointer;
        -webkit-appearance: none;
        margin-top: -5px;
    }

    input[type=range]:focus::-webkit-slider-runnable-track {
        background: #B6B6B6;
    }

    input[type=range]::-moz-range-track {
        width: 100%;
        height: 16px;
        cursor: pointer;
        box-shadow: 0px 0px 0px #000000;
        background: #B6B6B6;
        border-radius: 25px;
        border: 1px solid #8A8A8A;
    }

    input[type=range]::-moz-range-thumb {
        box-shadow: 1px 1px 1px #828282;
        border: 1px solid #8A8A8A;
        height: 24px;
        width: 35px;
        border-radius: 6px;
        background: #DADADA;
        cursor: pointer;
    }

    input[type=range]::-ms-track {
        width: 100%;
        height: 16px;
        cursor: pointer;
        background: transparent;
        border-color: transparent;
        color: transparent;
    }

    input[type=range]::-ms-fill-lower {
        background: #B6B6B6;
        border: 1px solid #8A8A8A;
        border-radius: 50px;
        box-shadow: 0px 0px 0px #000000;
    }

    input[type=range]::-ms-fill-upper {
        background: #B6B6B6;
        border: 1px solid #8A8A8A;
        border-radius: 50px;
        box-shadow: 0px 0px 0px #000000;
    }

    input[type=range]::-ms-thumb {
        margin-top: 1px;
        box-shadow: 1px 1px 1px #828282;
        border: 1px solid #8A8A8A;
        height: 24px;
        width: 35px;
        border-radius: 6px;
        background: #DADADA;
        cursor: pointer;
    }

    input[type=range]:focus::-ms-fill-lower {
        background: #B6B6B6;
    }

    input[type=range]:focus::-ms-fill-upper {
        background: #B6B6B6;
    }
</style>
